import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { useSelector } from "react-redux";
import PrivateRoute from "./components/PrivateRoute";
import { REPORT_USER_LIST } from "./constants/RouteConstants";
import { UserRole } from "./store/models/user.model";
import { IRootState } from "./store/reducers";
import AffiliateBuilder from "./views/AffiliateBuilder/AffiliateBuilder";
import Affiliates from "./views/Affiliates/Affiliates";
import AppContainer from "./views/AppContainer";
import ApplicationBuilder from "./views/ApplicationBuilder/ApplicationBuilder";
import CommentsDetail from "./views/ApplicationBuilder/sections/comments/CommentsDetail/CommentsDetail";
import AddGMEGSI from "./views/ApplicationBuilder/sections/GMEGSIDetails/AddGMEGSI/AddGMEGSI";
import MemoDetail from "./views/ApplicationBuilder/sections/memos/MemoDetail";
import RequirementDetail from "./views/ApplicationBuilder/sections/requirements/RequirementDetail/RequirementDetail";
import StatusHistory from "./views/ApplicationBuilder/StatusHistory";
import Applications from "./views/Applications/Applications";
import { BrokerContainer } from "./views/BrokerBuilder/BrokerContainer/BrokerContainer";
import Brokers from "./views/Brokers/Brokers";
import CampaignContainer from "./views/CampaignBuilder/CampaignContainer";
import CampaignDetails from "./views/CampaignDetail/CampaignDetail";
import CampaignDncEmail from "./views/CampaignDncEmail/CampaignDncEmail";
import CampaignDncEmailBuilder from "./views/CampaignDncEmailBuilder/CampaignDncEmailBuilder";
import CampaignDncPhone from "./views/CampaignDncPhone/CampaignDncPhone";
import CampaignDncPhoneBuilder from "./views/CampaignDncPhoneBuilder/CampaignDncPhoneBuilder";
import Campaigns from "./views/Campaigns/Campaigns";
import CommandCenter from "./views/CommandCenter/CommandCenter";
import Companies from "./views/Companies/Companies";
import CompanyBuilder from "./views/CompanyBuilder/CompanyBuilder";
import Dashboard from "./views/Dashboard/Dashboard";
import FileRef from "./views/FileRef/FileRef";
import FileUpload from "./views/FileUpload/FileUpload";
import ForgotPassword from "./views/ForgotPassword/ForgotPassword";
import HealthCheck from "./views/HealthCheck/HealthCheck";
import Login from "./views/Login/Login";
import Preproposals from "./views/Preproposals/Preproposals";
import ProductBuilder from "./views/ProductBuilder/ProductBuilder";
import Products from "./views/Products/Products";
import Profile from "./views/Profile/Profile";
import ProposalContainer from "./views/Proposals/Proposal/ProposalContainer";
import AllTable from "./views/Proposals/Proposals";
import Prospects from "./views/Prospects/Prospects";
import { ProspectsContainer } from "./views/ProspectsBuilder/ProspectsContainer/ProspectsContainer";
import { ReportDetail, ReportDetailBare, ReportDetailKiosk } from "./views/Reports/ReportDetail";
import Reports from "./views/Reports/Reports";
import ResetPassword from "./views/ResetPassword/ResetPassword";
import TemplateBuilder from "./views/TemplateBuilder/TemplateBuilder";
import Templates from "./views/Templates/Templates";
import UserBuilder from "./views/UserBuilder/UserBuilder";
import Users from "./views/Users/Users";

const getInitialRoute = (userRole: UserRole): string => {
  const propList = "/proposals/all";
  const appList = "/applications/all";
  const commandCenter = "/command-center";
  const initMap = {
    [UserRole.Admin]: propList,
    [UserRole.ProposalSpecialist]: propList,
    [UserRole.RegionalBranchManager]: propList,
    [UserRole.BranchManager]: propList,
    [UserRole.UnderwritingAssociate]: commandCenter,
  };
  return initMap[userRole] || propList;
};

const Routes = () => {
  const userRole = useSelector((state: IRootState) => state.auth.user?.userRole);
  return (
    <div className="app-routes">
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password/:userID/:uniqueResetCode" component={ResetPassword} />
        <Route path="/file-ref/:pid/:did" component={FileRef} />
        <Route path="/pub/reports/:id" component={ReportDetailBare} />
        <Route path="/kiosk/reports/:id" component={ReportDetailKiosk} />

        <Route
          path="/"
          render={props => (
            <AppContainer>
              <Switch>
                <PrivateRoute
                  path="/proposals/all"
                  component={AllTable}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/proposals/:id/(ml)?"
                  component={ProposalContainer}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/brokers/all"
                  component={Brokers}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/brokers/:id/(ml)?"
                  component={BrokerContainer}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/templates/all"
                  component={Templates}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/templates/:id"
                  component={TemplateBuilder}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/companies/all"
                  component={Companies}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/companies/:id"
                  component={CompanyBuilder}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/affiliates/all"
                  component={Affiliates}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                    UserRole.UnderwritingAssociate,
                  ]}
                />
                <PrivateRoute
                  path="/affiliates/:id"
                  component={AffiliateBuilder}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.RegionalBranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                    UserRole.UnderwritingAssociate,
                  ]}
                />
                <PrivateRoute path="/products/all" component={Products} authorizedRoles={[UserRole.Admin]} />
                <PrivateRoute path="/products/:id" component={ProductBuilder} authorizedRoles={[UserRole.Admin]} />
                <PrivateRoute path="/products/new" component={ProductBuilder} authorizedRoles={[UserRole.Admin]} />
                {/* // delete this */}
                <PrivateRoute
                  path="/product-builder/new"
                  component={ProductBuilder}
                  authorizedRoles={[UserRole.Admin]}
                />
                <PrivateRoute
                  path="/file-upload"
                  component={FileUpload}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.ProposalSpecialist,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute path="/health-check" component={HealthCheck} authorizedRoles={[UserRole.Admin]} />
                <PrivateRoute path="/users/all" component={Users} authorizedRoles={[UserRole.Admin]} />
                <PrivateRoute path="/users/:id" component={UserBuilder} authorizedRoles={[UserRole.Admin]} />
                <PrivateRoute path="/profile" component={Profile} />
                <PrivateRoute
                  path="/campaigns/new"
                  component={CampaignContainer}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/campaigns/all"
                  component={Campaigns}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/campaigns/:id"
                  component={CampaignDetails}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/preproposals/all"
                  component={Preproposals}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/dnc/email/all"
                  component={CampaignDncEmail}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/dnc/email/:id"
                  component={CampaignDncEmailBuilder}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/dnc/phone/all"
                  component={CampaignDncPhone}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/dnc/phone/:id"
                  component={CampaignDncPhoneBuilder}
                  authorizedRoles={[UserRole.Admin, UserRole.IOSpecialist]}
                />
                <PrivateRoute
                  path="/dashboard"
                  component={Dashboard}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.UnderwritingAssociate,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/command-center"
                  component={CommandCenter}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    ,
                  ]}
                />
                <PrivateRoute
                  path="/applications/all"
                  component={Applications}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:id/add-gme-gsi"
                  component={AddGMEGSI}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:appId/comments/:id"
                  component={CommentsDetail}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:appID/requirements/:reqID"
                  component={RequirementDetail}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:appID/memos/:memoID"
                  component={MemoDetail}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:appID/status-history"
                  component={StatusHistory}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/applications/:id"
                  component={ApplicationBuilder}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.UnderwritingAssociate,
                    UserRole.BranchManager,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                    UserRole.ProposalSpecialist,
                  ]}
                />

                <PrivateRoute
                  path="/reports/all"
                  component={Reports}
                  authorizedRoles={[UserRole.Admin, UserRole.BranchManager]}
                  authorizedUsers={REPORT_USER_LIST}
                />
                <PrivateRoute
                  path="/reports/:id"
                  component={ReportDetail}
                  authorizedRoles={[UserRole.Admin, UserRole.BranchManager]}
                  authorizedUsers={REPORT_USER_LIST}
                />

                <PrivateRoute
                  path="/prospects/all"
                  component={Prospects}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />
                <PrivateRoute
                  path="/prospects/:id"
                  component={ProspectsContainer}
                  authorizedRoles={[
                    UserRole.Admin,
                    UserRole.ProposalSpecialist,
                    UserRole.RegionalBranchManager,
                    UserRole.BranchManager,
                    UserRole.UnderwritingAssociate,
                    UserRole.Marketer,
                    UserRole.IOSpecialist,
                  ]}
                />

                {/* default */}
                <Redirect to={getInitialRoute(userRole)} />
              </Switch>
            </AppContainer>
          )}
        />
      </Switch>
    </div>
  );
};

export default Routes;
